import { useState, useEffect, useRef } from "react";
import { api } from "../services/api";
import { useHead } from "../services/common";
import { ChatMessage } from "../../types";

export default function Index() {
    useHead({
        title: "Kurz Inteligence",
        description: "AI rozhraní pro účastníky Kurzu Inteligence."
    });

    const [password, setPassword] = useState("");
    const [isAuth, setIsAuth] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState("");

    const [userName, setUserName] = useState("");
    const [nameStep, setNameStep] = useState(false);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState("");
    const [chatEnabled, setChatEnabled] = useState(true);
    const [conversationModeEnabled, setConversationModeEnabled] = useState(false);
    const [sending, setSending] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const shakeErrorRef = useRef<boolean>(false);
    const [shake, setShake] = useState(false);

    // 1. Check existing credentials on mount
    useEffect(() => {
        const checkSavedCredentials = async () => {
            const savedPwd = localStorage.getItem("user_pwd");
            const savedName = localStorage.getItem("user_name");

            if (savedPwd) {
                const [res, err] = await api.getIsUserPasswordCorrect({ password: savedPwd });
                if (res?.correct) {
                    setPassword(savedPwd);
                    setIsAuth(true);
                    if (savedName) {
                        setUserName(savedName);
                        // Load saved chat history
                        const savedHistory = localStorage.getItem(`chat_history_${savedName}`);
                        if (savedHistory) {
                            try {
                                setMessages(JSON.parse(savedHistory));
                            } catch (e) {
                                console.error("Error parsing saved chat history", e);
                            }
                        }
                    } else {
                        setNameStep(true);
                    }
                } else {
                    localStorage.removeItem("user_pwd");
                }
            }
            setAuthLoading(false);
        };

        checkSavedCredentials();
    }, []);

    // 2. Poll chat enabled status
    useEffect(() => {
        if (!isAuth) return;

        const checkStatus = async () => {
            const [res, err] = await api.getChatStatus({});
            if (res) {
                setChatEnabled(res.chatEnabled);
                setConversationModeEnabled(res.conversationModeEnabled);
            }
        };

        checkStatus();
        const interval = setInterval(checkStatus, 5000);
        return () => clearInterval(interval);
    }, [isAuth]);

    // 3. Scroll to bottom when messages list changes
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, sending]);

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError("");
        setShake(false);

        const [res, err] = await api.getIsUserPasswordCorrect({ password });
        if (res?.correct) {
            localStorage.setItem("user_pwd", password);
            setIsAuth(true);
            const savedName = localStorage.getItem("user_name");
            if (savedName) {
                setUserName(savedName);
                const savedHistory = localStorage.getItem(`chat_history_${savedName}`);
                if (savedHistory) {
                    try {
                        setMessages(JSON.parse(savedHistory));
                    } catch (e) {}
                }
            } else {
                setNameStep(true);
            }
        } else {
            setAuthError("Nesprávné přístupové heslo.");
            setShake(true);
            setTimeout(() => setShake(false), 5000);
        }
    };

    const handleNameSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = userName.trim();
        if (!trimmed) return;

        localStorage.setItem("user_name", trimmed);
        setUserName(trimmed);
        setNameStep(false);

        // Load or clear messages for this user name
        const savedHistory = localStorage.getItem(`chat_history_${trimmed}`);
        if (savedHistory) {
            try {
                setMessages(JSON.parse(savedHistory));
            } catch (e) {
                setMessages([]);
            }
        } else {
            setMessages([]);
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        const text = inputMessage.trim();
        if (!text || sending || !chatEnabled) return;

        const newUserMessage: ChatMessage = { role: "user", text };
        const updatedHistory = [...messages, newUserMessage];
        
        setMessages(updatedHistory);
        setInputMessage("");
        setSending(true);

        const [res, err] = await api.addChatResponse({
            name: userName,
            messages: updatedHistory,
            password: password
        });

        setSending(false);

        if (res) {
            const newModelMessage: ChatMessage = { role: "model", text: res.text };
            const finalHistory = [...updatedHistory, newModelMessage];
            setMessages(finalHistory);
            localStorage.setItem(`chat_history_${userName}`, JSON.stringify(finalHistory));
        } else if (err) {
            // Display error in chat as a system warning
            const errorMessage: ChatMessage = {
                role: "model",
                text: `⚠️ Chyba: ${err || "Nepodařilo se odeslat zprávu. Zkontrolujte připojení."}`
            };
            setMessages([...updatedHistory, errorMessage]);
        }
    };

    const handleNewConversation = () => {
        if (confirm("Opravdu chcete začít novou konverzaci? Historie bude vymazána.")) {
            setMessages([]);
            localStorage.removeItem(`chat_history_${userName}`);
        }
    };

    const handleLogout = () => {
        if (confirm("Chcete se odhlásit?")) {
            localStorage.removeItem("user_pwd");
            localStorage.removeItem("user_name");
            setIsAuth(false);
            setNameStep(false);
            setUserName("");
            setPassword("");
            setMessages([]);
        }
    };

    if (authLoading) {
        return (
            <div className="auth-container loading">
                <div className="spinner"></div>
                <p>Načítání inteligentního rozhraní...</p>
            </div>
        );
    }

    // STEP 1: PASSWORD PROMPT
    if (!isAuth) {
        return (
            <div className="auth-container">
                <div className={`auth-card ${shake ? "shake" : ""}`}>
                    <div className="brand">
                        <div className="brain-glow-logo"></div>
                        <h1>KURZ INTELIGENCE</h1>
                        <p>Zadejte vstupní heslo pro přístup do systému</p>
                    </div>

                    <form onSubmit={handlePasswordSubmit}>
                        <div className="input-group">
                            <input
                                type="password"
                                placeholder="Vstupní heslo"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoFocus
                                required
                            />
                        </div>
                        <button type="submit" className="glowing-btn">
                            Ověřit heslo
                        </button>
                        {authError && <div className="error-msg">{authError}</div>}
                    </form>
                </div>
            </div>
        );
    }

    // STEP 2: NAME ENTRY
    if (nameStep) {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <div className="brand">
                        <div className="user-glow-logo"></div>
                        <h1>Jak se jmenuješ?</h1>
                        <p>AI asistent s tebou bude komunikovat pod tímto jménem</p>
                    </div>

                    <form onSubmit={handleNameSubmit}>
                        <div className="input-group">
                            <input
                                type="text"
                                placeholder="Tvé jméno nebo přezdívka"
                                value={userName}
                                onChange={e => setUserName(e.target.value)}
                                autoFocus
                                required
                            />
                        </div>
                        <button type="submit" className="glowing-btn">
                            Vstoupit do kurzu
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // STEP 3: CHAT VIEW
    return (
        <div className="chat-app-container">
            <header className="chat-header">
                <div className="header-info">
                    <h2>AI Asistent</h2>
                    <div className="status-indicator">
                        <span className={`status-dot ${chatEnabled ? "active" : "disabled"}`}></span>
                        <span className="status-text">
                            {chatEnabled ? "AI Online" : "Chat vypnutý"}
                        </span>
                    </div>
                </div>

                <div className="user-info-bar">
                    <span className="user-name">Metoda: {userName}</span>
                    <button className="icon-btn logout-btn" onClick={handleLogout} title="Odhlásit se">
                        Odhlásit
                    </button>
                </div>
            </header>

            <main className="chat-messages-container">
                {messages.length === 0 ? (
                    <div className="welcome-screen">
                        <div className="welcome-icon">{conversationModeEnabled ? "💬" : "⚡"}</div>
                        {conversationModeEnabled ? (
                            <>
                                <h3>Režim nápovědy konverzace aktivní!</h3>
                                <p>AI asistent ti bude radit, co máš říct ostatním účastníkům.</p>
                                <div className="chat-instruction-banner">
                                    Napiš jako první zprávu, s kým komunikuješ (např. „Mluvím s Pavlem“ nebo jen jméno).
                                </div>
                            </>
                        ) : (
                            <>
                                <h3>Vítej v rozhraní Kurzu Inteligence!</h3>
                                <p>Napiš cokoliv, AI asistent ti odpoví. Přizpůsobí se průběhu představení.</p>
                            </>
                        )}
                        {!chatEnabled && (
                            <div className="chat-disabled-banner">
                                Chat je momentálně pozastaven organizátory. Vyčkejte na pokyny.
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="messages-list">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`message-row ${
                                    msg.role === "user" ? "user-row" : "model-row"
                                }`}
                            >
                                <div className="message-bubble">
                                    <div className="message-text">{msg.text}</div>
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="message-row model-row typing-row">
                                <div className="message-bubble typing-bubble">
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </main>

            <footer className="chat-footer-bar">
                {messages.length > 0 && (
                    <div className="chat-actions">
                        <button className="new-chat-btn" onClick={handleNewConversation}>
                            + Nová konverzace
                        </button>
                    </div>
                )}
                <form onSubmit={handleSendMessage} className="chat-input-form">
                    <input
                        type="text"
                        placeholder={
                            !chatEnabled
                                ? "Chat je momentálně pozastaven..."
                                : conversationModeEnabled
                                    ? "Napiš, s kým mluvíš nebo co ti píše..."
                                    : "Napiš zprávu pro AI..."
                        }
                        value={inputMessage}
                        onChange={e => setInputMessage(e.target.value)}
                        disabled={!chatEnabled || sending}
                        required
                    />
                    <button
                        type="submit"
                        disabled={!chatEnabled || sending || !inputMessage.trim()}
                        className="send-btn"
                    >
                        {sending ? "..." : "Odeslat"}
                    </button>
                </form>
            </footer>
        </div>
    );
}
